import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { ArrowBack, Refresh, Add, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import principalAPI from '../../services/principalAPI';
import { useAuth } from '../../context/AuthContext';
import MobileFormDialog from '@components/mobile/MobileFormDialog';

const TeacherWorkHours = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [workHours, setWorkHours] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTeacherFilter, setSelectedTeacherFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [formData, setFormData] = useState({
    workDate: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    console.log('Fetching work hours with params:', { startDate, endDate, selectedTeacherFilter });
    try {
      const params = { startDate, endDate };
      if (selectedTeacherFilter) params.teacherId = selectedTeacherFilter;
      const workHoursRes = await principalAPI.getTeachersWorkHours(params);
      console.log('Work hours response:', workHoursRes);
      setWorkHours(workHoursRes.data.workHours || []);
    } catch (error) {
      console.error('Error fetching work hours data:', error);
      console.error('Error details:', error.response || error.message);
      setWorkHours([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedTeacherFilter]);

  useEffect(() => {
    fetchTeachers();
  }, []);

  useEffect(() => {
    // Always fetch data, even if no teachers exist
    fetchData();
  }, [fetchData]);

  const fetchTeachers = async () => {
    try {
      const response = await principalAPI.getTeachers();
      setTeachers(response.data.teachers || []);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
    }
  };

  const handleOpenDialog = (teacher = null) => {
    setSelectedTeacher(teacher);
    setFormData({
      workDate: new Date().toISOString().split('T')[0],
      startTime: '',
      endTime: '',
      notes: ''
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTeacher(null);
    setFormData({
      workDate: new Date().toISOString().split('T')[0],
      startTime: '',
      endTime: '',
      notes: ''
    });
  };

  const handleSubmit = async () => {
    if (!selectedTeacher) return;

    try {
      await principalAPI.recordTeacherWorkHours(selectedTeacher.id, formData);
      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error('Error recording work hours:', error);
      alert('Failed to record work hours');
    }
  };

  const handleDelete = async (teacherId, workDate) => {
    if (!window.confirm('Are you sure you want to delete this work hours record?')) return;

    try {
      await principalAPI.deleteTeacherWorkHours(teacherId, { workDate });
      fetchData();
    } catch (error) {
      console.error('Error deleting work hours:', error);
      alert('Failed to delete work hours record');
    }
  };

  const calculateWorkDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'In Progress';
    
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diff = (end - start) / (1000 * 60 * 60); // hours
    
    const hours = Math.floor(diff);
    const minutes = Math.round((diff - hours) * 60);
    
    return `${hours}h ${minutes}m`;
  };

  const groupByTeacher = () => {
    const grouped = {};
    
    // Filter by selected teacher if one is selected
    const filteredWorkHours = selectedTeacherFilter 
      ? workHours.filter(wh => wh.teacher_id === selectedTeacherFilter)
      : workHours;
    
    filteredWorkHours.forEach(wh => {
      const teacherId = wh.teacher_id;
      if (!grouped[teacherId]) {
        grouped[teacherId] = {
          teacher: wh.teacher,
          records: []
        };
      }
      grouped[teacherId].records.push(wh);
    });

    return Object.values(grouped);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const groupedData = groupByTeacher();

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Teacher Work Hours
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/principal/dashboard')}
          >
            Back to Dashboard
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Date Range Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filter Work Hours
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="teacher-filter-label">Teacher</InputLabel>
                <Select
                  labelId="teacher-filter-label"
                  value={selectedTeacherFilter}
                  onChange={(e) => setSelectedTeacherFilter(e.target.value)}
                  label="Teacher"
                >
                  <MenuItem value="">
                    <em>All Teachers</em>
                  </MenuItem>
                  {teachers.map((teacher) => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      {teacher.user?.name || 'Unknown'} ({teacher.employee_id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="From Date"
                type="date"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="To Date"
                type="date"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={fetchData}
              >
                Apply Filter
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Card */}
      {groupedData.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Showing Results
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {groupedData.length} Teacher{groupedData.length !== 1 ? 's' : ''}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Records
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {groupedData.reduce((sum, g) => sum + g.records.length, 0)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Date Range
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Teachers with Work Hours */}
      {groupedData.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No work hours records found
          </Typography>
          <Typography color="text.secondary">
            {selectedTeacherFilter 
              ? `No records for selected teacher from ${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`
              : `No records for any teacher from ${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`
            }
          </Typography>
          <Box mt={2}>
            <Button 
              variant="outlined" 
              onClick={() => {
                setSelectedTeacherFilter('');
                setStartDate(new Date().toISOString().split('T')[0]);
                setEndDate(new Date().toISOString().split('T')[0]);
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>
      ) : (
        groupedData.map((group) => (
          <Card key={group.teacher?.id} sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={2}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {group.teacher?.user?.name || 'Unknown Teacher'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {group.teacher?.user?.email || 'N/A'} | Employee ID: {group.teacher?.employee_id || 'N/A'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog(group.teacher)}
                >
                  Add Hours
                </Button>
              </Box>

              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Start Time</strong></TableCell>
                      <TableCell><strong>End Time</strong></TableCell>
                      <TableCell><strong>Duration</strong></TableCell>
                      <TableCell><strong>Notes</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.records.map((record) => (
                      <TableRow key={record.id} hover>
                        <TableCell>{new Date(record.work_date).toLocaleDateString()}</TableCell>
                        <TableCell>{record.start_time}</TableCell>
                        <TableCell>
                          {record.end_time || (
                            <Chip label="In Progress" size="small" color="primary" />
                          )}
                        </TableCell>
                        <TableCell>
                          {calculateWorkDuration(record.start_time, record.end_time)}
                        </TableCell>
                        <TableCell>{record.notes || '-'}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(record.teacher_id, record.work_date)}
                            title="Delete"
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ))
      )}

      {/* Add/Edit Work Hours Dialog */}
      <MobileFormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={`Record Work Hours - ${selectedTeacher?.user?.name}`}
        actions={
          <>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!formData.startTime}
            >
              Save
            </Button>
          </>
        }
      >
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Work Date"
                type="date"
                fullWidth
                value={formData.workDate}
                onChange={(e) => setFormData({ ...formData, workDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Time"
                type="time"
                fullWidth
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Time"
                type="time"
                fullWidth
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Leave empty if still working"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                multiline
                rows={3}
                fullWidth
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes about the work day"
              />
            </Grid>
          </Grid>
        </Box>
      </MobileFormDialog>
    </Container>
  );
};

export default TeacherWorkHours;
